import React, { useState, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";

// Per-user escrow reconciliation: balances + diagnostics + full ledger.
//
// Confirmed lp-api response shapes (BC-1948):
//   balances    -> { total, available, escrow, tokenId }         (strings; 404 if no wallet)
//   diagnostics -> { currentEscrow, entries:[{type,count,totalAmount}], allEntries:[] }
//   history     -> { userId, entries:[{id,type,amount,tokenId,status,referenceId?,createdAt}], count }
//
// Ops: only manual-release is admin-usable. recalculate-balance and
// reset-balance are self-only on the backend (caller id must equal :userId) and
// would 403 for an admin acting on another user, so they are intentionally not
// surfaced here, see the PR note.
const BASE = "/admin/liquidity_pool_reconciliation";

const formatAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })
    : String(v ?? "-");
};

const asRows = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.entries)) return data.entries;
  if (data && Array.isArray(data.rows)) return data.rows;
  if (data && Array.isArray(data.allEntries)) return data.allEntries;
  return [];
};

const EscrowTab = ({ colors, gradients, showToast }) => {
  const [userId, setUserId] = useState("");
  const [activeUserId, setActiveUserId] = useState("");
  const [balances, setBalances] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [releaseModal, setReleaseModal] = useState(false);
  const [releaseReason, setReleaseReason] = useState("");
  const [releasing, setReleasing] = useState(false);

  const load = useCallback(
    async (id) => {
      const trimmed = (id || "").trim();
      if (!trimmed) {
        showToast("Enter a userId to look up escrow.", "warning");
        return;
      }
      setLoading(true);
      setActiveUserId(trimmed);
      setBalances(null);
      setDiagnostics(null);
      setHistory(null);

      // Fetch independently, a 404 on balances (user has no wallet) must not
      // hide diagnostics/history, so each call resolves on its own.
      const q = `?userId=${encodeURIComponent(trimmed)}`;
      const [bal, diag, hist] = await Promise.allSettled([
        getRequest(`${BASE}/escrow_balances${q}`),
        getRequest(`${BASE}/escrow_diagnostics${q}`),
        getRequest(`${BASE}/escrow_history${q}`),
      ]);

      if (bal.status === "fulfilled" && bal.value && bal.value.success) {
        setBalances(bal.value.data);
      }
      if (diag.status === "fulfilled" && diag.value && diag.value.success) {
        setDiagnostics(diag.value.data);
      }
      if (hist.status === "fulfilled" && hist.value && hist.value.success) {
        setHistory(hist.value.data);
      }
      if (bal.status === "rejected" && diag.status === "rejected" && hist.status === "rejected") {
        showToast(`No escrow data for ${trimmed} (all lookups failed).`, "danger");
      }
      setLoading(false);
    },
    [showToast]
  );

  const submitRelease = useCallback(async () => {
    if (!activeUserId) return;
    setReleasing(true);
    try {
      const res = await postRequest(`${BASE}/escrow_manual_release?userId=${encodeURIComponent(activeUserId)}`, {
        reason: releaseReason.trim() || undefined,
      });
      if (res && res.success) {
        showToast("Escrow manually released.", "success");
        setReleaseModal(false);
        setReleaseReason("");
        load(activeUserId);
      } else {
        throw new Error((res && res.error) || "Manual release failed");
      }
    } catch (err) {
      console.error(err);
      const msg = (err && err.data && err.data.error) || err.message || "Request failed";
      showToast("Manual release failed: " + msg, "danger");
    } finally {
      setReleasing(false);
    }
  }, [activeUserId, releaseReason, load, showToast]);

  const card = {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "16px",
    flex: "1 1 200px",
  };
  const th = { textAlign: "left", padding: "8px", borderBottom: `2px solid ${colors.border}`, fontSize: "12px", color: colors.gray600 };
  const td = { padding: "8px", borderBottom: `1px solid ${colors.gray300}`, fontSize: "13px" };
  const btn = (bg, disabled) => ({
    background: bg,
    color: colors.white,
    border: "none",
    borderRadius: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  });

  const historyRows = asRows(history);

  return (
    <div>
      {/* Lookup */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          load(userId);
        }}
        style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}
      >
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="Enter userId"
          style={{ flex: "1 1 320px", padding: "10px 12px", border: `1px solid ${colors.border}`, borderRadius: "6px", fontSize: "14px" }}
        />
        <button type="submit" style={btn(gradients.primary, loading)} disabled={loading}>
          {loading ? "Loading…" : "Look up escrow"}
        </button>
      </form>

      {!activeUserId && !loading && (
        <div style={{ padding: "40px", textAlign: "center", color: colors.gray500 }}>
          Enter a userId above to view its escrow balances, diagnostics and ledger.
        </div>
      )}

      {loading && <div style={{ padding: "40px", textAlign: "center", color: colors.gray500 }}>Loading escrow…</div>}

      {activeUserId && !loading && (
        <>
          {/* Balances */}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
            {balances ? (
              <>
                <div style={card}>
                  <div style={{ fontSize: "12px", color: colors.gray600 }}>Total</div>
                  <div style={{ fontSize: "22px", fontWeight: 700 }}>{formatAmount(balances.total)}</div>
                </div>
                <div style={card}>
                  <div style={{ fontSize: "12px", color: colors.gray600 }}>Available</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: colors.success }}>{formatAmount(balances.available)}</div>
                </div>
                <div style={card}>
                  <div style={{ fontSize: "12px", color: colors.gray600 }}>In Escrow</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: colors.accent }}>{formatAmount(balances.escrow)}</div>
                </div>
                <div style={card}>
                  <div style={{ fontSize: "12px", color: colors.gray600 }}>Token</div>
                  <div style={{ fontSize: "16px", fontWeight: 600, marginTop: "6px" }}>{balances.tokenId || "-"}</div>
                </div>
              </>
            ) : (
              <div style={{ ...card, flex: "1 1 100%", color: colors.gray500 }}>
                No escrow balance for <strong>{activeUserId}</strong> (the user may have no wallet yet).
              </div>
            )}
          </div>

          {/* Ops, only manual-release is admin-usable (see file header) */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
            <button style={btn(gradients.accent, releasing)} disabled={releasing} onClick={() => setReleaseModal(true)}>
              Release held escrow
            </button>
          </div>

          {/* Diagnostics */}
          <h4 style={{ margin: "0 0 8px", color: colors.primary }}>Diagnostics</h4>
          {diagnostics ? (
            <>
              {diagnostics.currentEscrow !== undefined && (
                <div style={{ marginBottom: "10px", fontSize: "14px" }}>
                  Current escrow: <strong>{formatAmount(diagnostics.currentEscrow)}</strong>
                </div>
              )}
              <DiagnosticsTable diagnostics={diagnostics} th={th} td={td} colors={colors} />
            </>
          ) : (
            <div style={{ color: colors.gray500, marginBottom: "20px" }}>No diagnostics returned.</div>
          )}

          {/* History */}
          <h4 style={{ margin: "20px 0 8px", color: colors.primary }}>Escrow ledger ({historyRows.length})</h4>
          {historyRows.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {Object.keys(historyRows[0]).map((k) => (
                      <th key={k} style={th}>{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row, i) => (
                    <tr key={row.id || i}>
                      {Object.keys(historyRows[0]).map((k) => (
                        <td key={k} style={td}>{typeof row[k] === "object" ? JSON.stringify(row[k]) : String(row[k] ?? "-")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: colors.gray500 }}>No ledger entries.</div>
          )}
        </>
      )}

      {/* Manual release modal */}
      {releaseModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => !releasing && setReleaseModal(false)}
        >
          <div style={{ background: colors.white, borderRadius: "10px", width: "440px", maxWidth: "90vw", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ background: gradients.accent, color: colors.white, padding: "16px 20px", fontWeight: 700 }}>Release held escrow</div>
            <div style={{ padding: "20px" }}>
              <p style={{ marginTop: 0 }}>
                Release held escrow back to <strong>{activeUserId}</strong>.
              </p>
              <label style={{ fontSize: "13px", color: colors.gray600 }}>Reason (optional)</label>
              <textarea
                value={releaseReason}
                onChange={(e) => setReleaseReason(e.target.value)}
                rows={3}
                style={{ width: "100%", padding: "8px", border: `1px solid ${colors.border}`, borderRadius: "6px", marginTop: "4px", marginBottom: "12px" }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <button onClick={() => setReleaseModal(false)} disabled={releasing} style={{ background: colors.gray200, color: colors.gray700, border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={submitRelease} disabled={releasing} style={btn(colors.accent, releasing)}>
                  {releasing ? "Releasing…" : "Release escrow"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// diagnostics.entries -> [{ type, count, totalAmount }]; fall back to JSON for
// anything unexpected so a shape change never throws.
const DiagnosticsTable = ({ diagnostics, th, td, colors }) => {
  const rows = Array.isArray(diagnostics.entries) ? diagnostics.entries : asRows(diagnostics);
  if (rows.length > 0) {
    const keys = Object.keys(rows[0]);
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{keys.map((k) => <th key={k} style={th}>{k}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.type || i}>
                {keys.map((k) => (
                  <td key={k} style={td}>{typeof row[k] === "object" ? JSON.stringify(row[k]) : String(row[k] ?? "-")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <pre style={{ background: colors.gray100, border: `1px solid ${colors.border}`, borderRadius: "6px", padding: "12px", fontSize: "12px", overflowX: "auto" }}>
      {JSON.stringify(diagnostics, null, 2)}
    </pre>
  );
};

export default EscrowTab;
